import queryString from 'query-string'
import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import agent from 'agent'
import authentication from 'api/authentication'
import Email from 'components/authentication/form-fields/Email'
import SetPassword from 'components/authentication/form-fields/SetPassword'
import SAMLRequired from 'components/authentication/registration/new-user/SAMLRequired'
import { SignedOutForm } from 'components/authentication/SignedOutForm'
import SignedOutFormContainer from 'components/authentication/SignedOutFormContainer'
import { UpperBackText } from 'components/authentication/UpperBackText'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import theme from 'theme'
import { track, identify } from 'track'

const STAGE_FORM_DETAILS = {
  email: {
    title: 'Create Your Account'
  },
  password: {
    title: 'Set your password',
    back: true
  },
  saml: {
    title: 'SAML Required'
  }
}

const FORM_STAGES = {
  EMAIL: 'email',
  PASSWORD: 'password',
  SAML_REQUIRED: 'saml'
}

function trackSubmission(email) {
  identify(email)
  track('RegistrationSubmission', {
    userId: email,
    role: 'user'
  })
}

export const NewUser: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const authState = useAuth()
  const parsed = queryString.parse(location.search)

  const [state, setState] = useState({
    code: parsed.code,
    companyId: parsed.companyId || '',
    companyName: parsed.companyName || '',
    confirmPassword: '',
    email: parsed.email || '',
    formStage: FORM_STAGES.EMAIL,
    fullName: '',
    orgId: parsed.orgId || '',
    password: '',
    privacyPolicyAccepted: false,
    samlMessage: '',
    submitting: false
  })

  const updateState = (newState) => {
    setState((prevState) => ({ ...prevState, ...newState }))
  }

  const handleChange = ({ target }) => {
    updateState({ [target.name]: target.value })
  }

  const handleBack = () => {
    updateState({ formStage: FORM_STAGES.EMAIL })
  }

  const submitDetails = async () => {
    const { companyId, code, email, password } = state
    const role = 'USER'

    updateState({ submitting: true })

    let session
    try {
      const res = await agent.post(`/companies/${companyId}/users`, {
        code,
        email,
        password,
        role,
        allow_us_storage: true
      })
      session = res.data

      if (session.result === 'SAML_REQUIRED') {
        updateState({
          formStage: FORM_STAGES.SAML_REQUIRED,
          submitting: false,
          samlMessage: session.message
        })
        trackSubmission(email)
        return
      }

      trackSubmission(email)
      updateState({ submitting: false })
      if (session) {
        await authState.startSession(session)

        toast({
          type: 'success',
          text: 'Account Created, Welcome to Gremlin'
        })

        navigate('/')
      }
    } catch (err) {
      console.error(err)
      updateState({ submitting: false })

      toast({
        type: 'error',
        text:
          (err.response || {}).data ||
          'There was an issue, Please try again later'
      })
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    switch (state.formStage) {
      case FORM_STAGES.EMAIL:
        return updateState({ formStage: FORM_STAGES.PASSWORD })
      case FORM_STAGES.PASSWORD:
        return submitDetails()
    }
  }

  const handleAcceptPrivacyPolicyChange = () => {
    updateState({
      privacyPolicyAccepted: !state.privacyPolicyAccepted
    })
  }

  const {
    formStage,
    email,
    password,
    confirmPassword,
    privacyPolicyAccepted,
    submitting,
    samlMessage,
    companyName
  } = state

  const { title, back } = STAGE_FORM_DETAILS[formStage]

  return (
    <SignedOutFormContainer>
      <UpperBackText onClick={back && handleBack}>Back</UpperBackText>
      <SignedOutForm
        // handleBack={back && handleBack}
        title={title}
        onSubmit={handleSubmit}
      >
        {formStage === FORM_STAGES.EMAIL && (
          <Email email={email} inputIsDisabled={true} />
        )}
        {formStage === FORM_STAGES.PASSWORD && (
          <SetPassword
            confirmPassword={confirmPassword}
            css={{
              marginBottom: theme.space[4]
            }}
            handleAcceptPrivacyPolicyChange={handleAcceptPrivacyPolicyChange}
            handleChange={handleChange}
            includeSamlOption={true}
            isLoading={submitting}
            password={password}
            privacyPolicyAccepted={privacyPolicyAccepted}
            samlUrl={authentication.generateSamlUrl({ companyName })}
          />
        )}
        {formStage === FORM_STAGES.SAML_REQUIRED && (
          <SAMLRequired
            message={samlMessage}
            samlUrl={authentication.generateSamlUrl({ companyName: undefined })}
          />
        )}
      </SignedOutForm>
    </SignedOutFormContainer>
  )
}
